import React, { useState } from "react";
import {
  Avatar,
  Button,
  Card,
  Title,
  Paragraph,
  Divider,
  useTheme,
  RadioButton,
  TouchableRipple,
} from "react-native-paper";
import { View, StyleSheet } from "react-native";
import { ThemeType } from "../../theme/theme";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Inline } from "../layout/inline";
import { Spacer } from "../spacer/spacer";
import upperFirst from "lodash.upperfirst";
import { Text } from "../../components/index";
import { PaymentMethodMoneygram } from "./payment-method-moneygram";
import toLower from "lodash.tolower";
import { PaymentSourceType } from "../../enums";
import { useSelector } from "react-redux";
import { Stack } from "../layout/stack";
import {
  isWithinCertifiedDateRange,
  shouldDisablePayment,
} from "../../hooks/usePaymentMethodInformation";
import { useAuthData } from "../../hooks/useAuthData";
import { testProps } from "../../utils/testProps";

interface SinglePaymentSource {
  paymentType: PaymentSourceType;
  allow_cof: boolean;
  id: number | string;
  street_address: string | null;
  address_line2: string | null;
  city: string | null;
  last_four_digits: string;
  state: string | null;
  cardholder: string;
  zip: number;
  card_type: "VISA" | "MASTERCARD";
}
interface PaymentMethodCardProps extends SinglePaymentSource {
  onPress: any;
  navigation: any;
  fees: PropertyFees;
  allowedPayments: "ANY" | "DO_NOT_ACCEPT_PAYMENT" | "CASH_EQUIVALENT";
  index: number;
  bank_account_type: string;
  shouldDisableCC?: boolean;
  allowCC?: boolean;
  allowACH?: boolean;
  id: string | number;
  certifiedDateRange: any;
  emitSelected: any;
  selected: any;
}
interface PropertyFees extends Record<string, any> {
  // In cents
  certified_check_limit: number;
  id: string | number;
  // in cents
  non_certified_limit: number;
  // in cents
  rent_ach_minimum: number;
  rent_ach_percent: number;
  // floating number as percent 2.25
  rent_cc_percent: number;
  rent_cc_minimum: number;
  // floating number as percent
  rent_certified_cc_percent: number;
  rent_domuso_cash_minimum: number;
}

export const PaymentMethodCard = (cardProps: PaymentMethodCardProps) => {
  const theme = useTheme() as ThemeType;
  const {
    card_type,
    cardholder,
    paymentType,
    fees = {},
    street_address,
    address_line2,
    city,
    state,
    shouldDisableCC = false,
    allowCC = true,
    allowACH = true,
    zip,
    index,
    last_four_digits,
    bank_account_type,
    id,
    selected,
    certifiedDateRange,
    emitSelected,
  } = cardProps;
  const fullName =
    cardholder && cardholder.length > 23 ? `${cardholder.substring(0, 21)}...` : cardholder; // for really long names
  const setSelectedWrapped = () => {
    emitSelected({
      id,
      type: paymentType,
      paymentType,
      card_type,
      last_four_digits,
      bank_account_type,
      street_address,
      address_line2,
      city,
      state,
      zip,
    });
  };
  const checkedStatus = Boolean(selected?.id === id) ? "checked" : "unchecked";
  const { rent_cc_percent, rent_ach_minimum }: any = fees;
  const withinCertifiedDateRange =
    certifiedDateRange && isWithinCertifiedDateRange(certifiedDateRange);
  switch (paymentType) {
    case PaymentSourceType.BANK_ACCOUNT:
      return (
        <PaymentMethodBankAccount
          {...cardProps}
          setSelectedWrapped={setSelectedWrapped}
          rentAchMinimum={rent_ach_minimum}
          allowACH={allowACH}
          withinCertifiedDateRange={withinCertifiedDateRange}
          checkedStatus={checkedStatus}
          fullName={fullName}
        />
      );
    case PaymentSourceType.CREDIT_CARD:
      return (
        <PaymentMethodCreditCard
          {...cardProps}
          setSelectedWrapped={setSelectedWrapped}
          rentCCPercent={rent_cc_percent}
          shouldDisableCC={shouldDisableCC}
          allowCC={allowCC}
          withinCertifiedDateRange={withinCertifiedDateRange}
          checkedStatus={checkedStatus}
          fullName={fullName}
        />
      );
    case PaymentSourceType.MONEYGRAM:
      return (
        <PaymentMethodMoneygram
          {...cardProps}
          setSelectedWrapped={setSelectedWrapped}
          checkedStatus={checkedStatus}
        />
      );
  }
};

export const PaymentMethodBankAccount = (bankAccountProps: any) => {
  const theme = useTheme() as ThemeType;
  let {
    active,
    last_four_digits,
    bank_account_type,
    allowedPayments,
    useRadioButton = true,
    withinCertifiedDateRange,
    checkedStatus,
    setSelectedWrapped,
    fullName,
    allowACH = true,
  } = bankAccountProps;
  const isCertifiedMethod = false;
  let disabledMethod =
    shouldDisablePayment({ isCertifiedMethod, allowedPayments }) || withinCertifiedDateRange;
  if (!allowACH) {
    disabledMethod = true;
    useRadioButton = false;
  }
  const styles = getStyles(disabledMethod);

  if (!useRadioButton) {
    return (
      <>
        <TouchableRipple rippleColor="rgba(0, 0, 0, .32)" disabled={disabledMethod}>
          <View style={styles.cardContainer}>
            <Stack space="tiny">
              <Text style={styles.title}>{upperFirst(toLower(bank_account_type))}</Text>
              <Text style={active ? styles.active : styles.inactive}>
                {active ? "Active" : "Inactive"}
              </Text>
            </Stack>
            <Stack space="tiny">
              <Text>**** **** **** {last_four_digits}</Text>
              <Text style={{ textAlign: "right" }}>{fullName}</Text>
            </Stack>
          </View>
        </TouchableRipple>
        <Spacer size="smaller"></Spacer>
        <Divider />
      </>
    );
  }

  return (
    <>
      <TouchableRipple rippleColor="rgba(0, 0, 0, .32)" disabled={disabledMethod}>
        <View style={styles.cardContainer} accessible={true}>
          {useRadioButton ? (
            <RadioButton.Android
              value="first"
              color={theme.colors.darkBg}
              status={checkedStatus}
              onPress={setSelectedWrapped}
              {...testProps(`bank-account-option-${last_four_digits}`)}
            />
          ) : null}
          <View style={styles.cardViewBA}>
            <Inline style={styles.cardRow}>
              <Text style={styles.title}>{upperFirst(toLower(bank_account_type))}</Text>
              <Text>**** **** **** {last_four_digits}</Text>
            </Inline>
            <Inline style={styles.cardRow}>
              <Text style={active ? styles.active : styles.inactive}>
                {active ? "Active" : "Inactive"}
              </Text>
              <Text>{fullName}</Text>
            </Inline>
          </View>
        </View>
      </TouchableRipple>
      <Divider />
    </>
  );
};

export const PaymentMethodCreditCard = (creditCardProps: any) => {
  let {
    active,
    checkedStatus,
    card_type,
    shouldDisableCC = false,
    allowCC = true,
    useRadioButton = true,
    allowedPayments,
    withinCertifiedDateRange,
    last_four_digits,
    setSelectedWrapped,
    fullName,
  } = creditCardProps;
  const theme = useTheme() as ThemeType;
  // TODO: when certified methods exist
  const isCertifiedMethod = false;
  let disabledMethod =
    shouldDisablePayment({ isCertifiedMethod, allowedPayments }) || withinCertifiedDateRange;
  if (shouldDisableCC || !allowCC) {
    disabledMethod = true;
    useRadioButton = false;
  }
  const styles = getStyles(disabledMethod);

  if (!useRadioButton) {
    return (
      <>
        <TouchableRipple rippleColor="rgba(0, 0, 0, .32)" disabled={disabledMethod}>
          <View style={styles.cardContainer}>
            <Inline>
              <MaterialCommunityIcons
                name="credit-card-outline"
                // style={styles.ccIcon}
                size={theme.iconSize.medium}
                color={disabledMethod ? theme.colors.disabled : "black"}
              />
              <Stack space="tiny">
                <Text style={styles.title}>{card_type}</Text>
                <Text style={active ? styles.active : styles.inactive}>
                  {active ? "Active" : "Inactive"}
                </Text>
              </Stack>
            </Inline>
            <Stack space="tiny">
              <Text style={styles.lastFourDigits}>**** **** **** {last_four_digits}</Text>
              <Text style={styles.fullName}>{fullName}</Text>
            </Stack>
          </View>
        </TouchableRipple>
        <Spacer size="smaller" />
        <Divider />
      </>
    );
  }

  return (
    <>
      <TouchableRipple rippleColor="rgba(0, 0, 0, .32)" disabled={disabledMethod}>
        <View style={styles.cardContainer} accessible={true}>
          <RadioButton.Android
            color={theme.colors.darkBg}
            value="first"
            status={checkedStatus}
            onPress={setSelectedWrapped}
            {...testProps(`credit-card-option-${last_four_digits}`)}
          />
          <MaterialCommunityIcons
            name="credit-card-outline"
            style={styles.ccIcon}
            size={theme.iconSize.medium}
            color="black"
          />
          {/* MasterCard is a bit long so we have to override the width a bit */}
          <View style={[styles.cardViewCC, { width: "80%" }]}>
            <Inline style={styles.cardRow}>
              <Text style={styles.title}>{card_type}</Text>
              <Text style={styles.lastFourDigits}>**** **** **** {last_four_digits}</Text>
            </Inline>
            <Inline style={styles.cardRow}>
              <Text style={active ? styles.active : styles.inactive}>
                {active ? "Active" : "Inactive"}
              </Text>
              <Text>{fullName}</Text>
            </Inline>
          </View>
        </View>
      </TouchableRipple>
      <Divider />
    </>
  );
};

// TODO: do somehting like radiobutton less and use tht
const getStyles = (disabledMethod: boolean) => {
  const theme = useTheme() as ThemeType;
  const cardView = StyleSheet.create({
    style: {
      display: "flex",
      flexDirection: "column",
    },
  });
  return StyleSheet.create({
    cardContainer: {
      width: "100%",
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      flex: 1,
      paddingTop: 0,
      paddingBottom: 4,
    },
    ccIcon: {
      width: "10%",
    },
    cardViewBA: {
      ...cardView.style,
      width: "80%",
    },
    cardViewCC: {
      ...cardView.style,
      width: "70%",
    },
    cardRow: {
      display: "flex",
      flex: 1,
      justifyContent: "space-between",
    },
    title: {
      fontWeight: "bold",
      fontSize: theme.fontSize.medium,
      ...(disabledMethod ? { color: theme.colors.disabled } : {}),
    },
    active: {
      color: theme.colors.green,
      ...(disabledMethod ? { color: theme.colors.disabled } : {}),
    },
    inactive: {
      color: theme.colors.error,
      ...(disabledMethod ? { color: theme.colors.disabled } : {}),
    },
    lastFourDigits: {
      ...(disabledMethod ? { color: theme.colors.disabled } : {}),
    },
    fullName: {
      textAlign: "right",
      ...(disabledMethod ? { color: theme.colors.disabled } : {}),
    },
  });
};
